import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import {
  replyEmail,
  draftEmail,
  fetchSendGuard,
  ApiError,
  type ReplyResult,
  type SendGuard,
} from "../../api";
import { buildOutgoingEmailHtml } from "@edge/shared";

interface Party {
  name: string;
  email: string;
}
interface Props {
  parent: {
    id: string;
    subject: string;
    from: Party;
    to: Party[];
    cc: Party[];
    /** Original message body — AI context for the draft modes (Stage 3). */
    body?: string;
  };
  onClose: () => void;
  onSent: (result: ReplyResult) => void;
  /** EM-1: the deal this email belongs to. When set, the AI bar offers "From
   *  docs" (read the deal's WorkDrive docs into the reply). Omitted in the
   *  non-deal cockpit, where the button is hidden. */
  dealId?: string;
}

type AiMode = "generate" | "polish" | "edit";

function fmtParty(p: Party): string {
  return p.name ? `${p.name} <${p.email}>` : p.email;
}

/**
 * Email reply composer + the APPROVE-BEFORE-SEND gate (Piece 2, Stage 2 — plain
 * reply, no AI yet; the AI modes layer onto this in Stage 3). Nothing sends
 * until the user confirms the gate, which shows the exact final To/Cc/subject/
 * body. All sends route through the one gated worker route (POST /:id/reply),
 * which itself holds the self-test send guard server-side.
 */
export function EmailReplyComposer({ parent, onClose, onSent, dealId }: Props) {
  const [text, setText] = useState("");
  const [replyAll, setReplyAll] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guard, setGuard] = useState<SendGuard | null>(null);
  // AI draft state (Stage 3). aiBusy = which mode is running, or null.
  const [aiBusy, setAiBusy] = useState<AiMode | "docs" | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");
  // EM-1: which docs the last "From docs" draft used / looked at.
  const [docInfo, setDocInfo] = useState<{
    used: string[];
    looked_at: string[];
    no_match: boolean;
  } | null>(null);
  // One idempotency key per composer session — a double-confirm can't double-send.
  const idemRef = useRef<string>(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
  );

  useEffect(() => {
    fetchSendGuard().then(setGuard).catch(() => {});
  }, []);

  const subject = parent.subject.toLowerCase().startsWith("re:")
    ? parent.subject
    : `Re: ${parent.subject}`;

  // Recipient preview. Outlook's createReply resolves the authoritative list
  // server-side; this mirrors it for the gate. Reply → sender only. Reply-all →
  // sender + the thread's other recipients (minus the sender).
  const recipients = useMemo(() => {
    const to = [parent.from];
    const cc = replyAll
      ? [...parent.to, ...parent.cc].filter(
          (r) => r.email && r.email.toLowerCase() !== parent.from.email.toLowerCase(),
        )
      : [];
    return { to, cc };
  }, [parent, replyAll]);

  // ── AI draft modes. Output is plain text → it just fills the textarea, which
  // stays hand-editable. The AI never sends; everything still flows the gate. ──
  const threadContext = useMemo(
    () => ({
      subject: parent.subject,
      from: fmtParty(parent.from),
      body: parent.body ?? "",
    }),
    [parent],
  );

  const runAi = async (mode: AiMode, useDocs = false) => {
    setAiBusy(useDocs ? "docs" : mode);
    setAiError(null);
    if (useDocs) setDocInfo(null);
    try {
      const { draft, docs } = await draftEmail({
        mode,
        thread_context: threadContext,
        current_draft: text.trim() || undefined,
        // For "From docs" the instruction is the query/brief; for edit it's the change.
        instruction: useDocs || mode === "edit" ? instruction.trim() || undefined : undefined,
        use_docs: useDocs || undefined,
        deal_id: useDocs ? dealId : undefined,
      });
      setText(draft);
      if (mode === "edit") setInstruction("");
      if (useDocs && docs) setDocInfo(docs);
    } catch (err) {
      setAiError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "draft_failed",
      );
    } finally {
      setAiBusy(null);
    }
  };

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await replyEmail(parent.id, {
        body: text.trim(),
        reply_all: replyAll,
        idempotency_key: idemRef.current,
      });
      onSent(result);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "send_failed",
      );
      setSending(false);
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card email-reply-card">
        <div className="email-detail-header">
          <div className="write-confirm-title">
            {confirming ? "CONFIRM SEND" : "REPLY"}
          </div>
          <button className="email-detail-close" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        {guard?.active && (
          <div className="email-guard-banner">
            ⚠ SEND GUARD ACTIVE — every send is redirected to{" "}
            <b>{guard.redirect_to}</b>. No email will reach the real recipient.
          </div>
        )}

        {!confirming ? (
          // ── Compose ──
          <div className="email-reply-body">
            <div className="email-reply-meta">
              <div className="email-detail-row">
                <span className="email-detail-label">To</span>
                <span className="email-detail-value">
                  {recipients.to.map(fmtParty).join(", ")}
                </span>
              </div>
              {recipients.cc.length > 0 && (
                <div className="email-detail-row">
                  <span className="email-detail-label">Cc</span>
                  <span className="email-detail-value">
                    {recipients.cc.map(fmtParty).join(", ")}
                  </span>
                </div>
              )}
              <div className="email-detail-row">
                <span className="email-detail-label">Subject</span>
                <span className="email-detail-value email-detail-subject">{subject}</span>
              </div>
            </div>

            <label className="email-reply-replyall">
              <input
                type="checkbox"
                checked={replyAll}
                onChange={(e) => setReplyAll(e.target.checked)}
              />
              Reply all (include everyone on the thread)
            </label>

            <div className="email-reply-ai">
              <div className="email-reply-ai-row">
                <span className="email-reply-ai-label">✨ AI</span>
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("generate")}
                  disabled={!!aiBusy}
                  title="AI reads the thread and writes a complete reply"
                >
                  {aiBusy === "generate" ? "Drafting…" : "Draft for me"}
                </button>
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("polish")}
                  disabled={!!aiBusy || !text.trim()}
                  title={
                    text.trim()
                      ? "AI professionalizes your draft, keeping every specific"
                      : "Write a rough draft first"
                  }
                >
                  {aiBusy === "polish" ? "Polishing…" : "Polish my draft"}
                </button>
              </div>
              <div className="email-reply-ai-row">
                <input
                  className="email-reply-ai-input"
                  placeholder="Tell the AI a change (e.g. “shorter, add I’ll send docs Friday”)…"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && instruction.trim() && text.trim() && !aiBusy) {
                      e.preventDefault();
                      void runAi("edit");
                    }
                  }}
                  disabled={!!aiBusy}
                />
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("edit")}
                  disabled={!!aiBusy || !text.trim() || !instruction.trim()}
                  title={
                    !text.trim()
                      ? "Draft something first"
                      : !instruction.trim()
                        ? "Type a change above"
                        : "AI revises the draft and returns the full text"
                  }
                >
                  {aiBusy === "edit" ? "Revising…" : "Revise"}
                </button>
                {dealId && (
                  <button
                    className="email-reply-ai-btn email-reply-ai-docs"
                    onClick={() => void runAi("generate", true)}
                    disabled={!!aiBusy || !instruction.trim()}
                    title={
                      instruction.trim()
                        ? "AI reads the deal's documents and drafts the reply using them"
                        : "Type what to write/find above (e.g. “confirm the appraised value”)"
                    }
                  >
                    {aiBusy === "docs" ? "📄 Reading documents…" : "📄 From docs"}
                  </button>
                )}
              </div>
              {aiBusy === "docs" && (
                <div className="email-reply-ai-docnote">
                  Reading the deal’s documents… scanned files can take ~20s.
                </div>
              )}
              {docInfo && aiBusy !== "docs" && (
                <div className="email-reply-ai-docsrc">
                  {docInfo.used.length > 0
                    ? `📄 Drafted using: ${docInfo.used.join(", ")}`
                    : docInfo.no_match
                      ? "📄 No matching document found — drafted from the thread. Try naming the doc or being more specific."
                      : `📄 Looked at ${docInfo.looked_at.join(", ")} but couldn’t read them — drafted from the thread.`}
                </div>
              )}
              {aiError && <div className="email-reply-ai-error">⚠ AI: {aiError}</div>}
              <div className="email-reply-ai-note">
                AI fills the box below — you can hand-edit it. The AI never sends;
                every send still goes through the review gate.
              </div>
            </div>

            <textarea
              className="email-reply-textarea"
              placeholder="Write your reply, or use AI above…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              autoFocus
            />
            <div className="email-reply-sig-note">
              Your WestEdge signature is appended automatically, and the original
              message is quoted below your reply.
            </div>

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button className="email-detail-close-btn" onClick={onClose}>
                CANCEL
              </button>
              <button
                className="write-confirm-confirm"
                disabled={!text.trim()}
                onClick={() => setConfirming(true)}
                title={text.trim() ? "Review before sending" : "Write a reply first"}
              >
                REVIEW & SEND →
              </button>
            </div>
          </div>
        ) : (
          // ── Approve-before-send gate ──
          <div className="email-reply-body">
            <div className="email-reply-gate-note">
              This is exactly what will be sent. Nothing has been sent yet.
            </div>
            <div className="email-reply-meta">
              <div className="email-detail-row">
                <span className="email-detail-label">To</span>
                <span className="email-detail-value">
                  {recipients.to.map(fmtParty).join(", ")}
                </span>
              </div>
              {recipients.cc.length > 0 && (
                <div className="email-detail-row">
                  <span className="email-detail-label">Cc</span>
                  <span className="email-detail-value">
                    {recipients.cc.map(fmtParty).join(", ")}
                  </span>
                </div>
              )}
              <div className="email-detail-row">
                <span className="email-detail-label">Subject</span>
                <span className="email-detail-value email-detail-subject">{subject}</span>
              </div>
            </div>

            {/* WYSIWYG, same white "paper" surface as compose. The reply body
                (your text + thank-you closing + full signature) is byte-identical
                to what the worker sends — same @edge/shared builder. The worker
                then appends Graph's quoted thread; we show the original message
                below as it'll be quoted. */}
            <div className="email-html-preview">
              <div
                dangerouslySetInnerHTML={{
                  __html: buildOutgoingEmailHtml(text.trim()),
                }}
              />
              <div className="email-preview-quote">
                <div className="email-preview-quote-label">
                  ↳ Original message, quoted below your reply:
                </div>
                <div className="email-preview-quote-body">
                  {parent.body?.trim() || "(original message)"}
                </div>
              </div>
            </div>

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button
                className="email-detail-close-btn"
                onClick={() => setConfirming(false)}
                disabled={sending}
              >
                ← BACK
              </button>
              <button
                className={clsx("write-confirm-confirm", guard?.active && "is-guarded")}
                onClick={() => void send()}
                disabled={sending}
              >
                {sending
                  ? "SENDING…"
                  : guard?.active
                    ? `SEND (→ ${guard.redirect_to})`
                    : "SEND"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
